import Scrollspy from 'react-scrollspy-ez';
import styled from 'styled-components';

import ContentHeaderStyled from '../../../components/ContentHeader';
import ExternalLinkGroupStyled from '../../../components/ExternalLinkGroup';
import PaginationStyled from '../../../components/Pagination';
import { REFERENCE_LINKS } from '../../../constants';
import commonPageContentStyles, {
  textStyle,
} from '../../../styles/mixins/_commonPageContentStyles';
import navigationStyles from '../../../styles/mixins/navigationMenuStyles';

interface Props {
  className?: string;
}

const IMAGES = '/images/branding-styleguide-brand-in-use';

const examples = [
  {
    id: 'department',
    title: 'Department Branding',
    description:
      'Materials led by the DHCS department identity — the DHCS logo, DHCS Blue header fields, and the full brand color palette. Used for department-wide communications, recruitment, and public events.',
    image: {
      src: `${IMAGES}/department-branding@2x.webp`,
      alt: 'Department-branded flyers and signage led by the DHCS logo and DHCS Blue header fields',
      width: 451,
      height: 252,
    },
    caption: 'Flyer',
    captionText:
      "ECM benefit, County Behavioral Health ratings, Benefits & Wellness Fair, Home & Community-Based waivers. Signage: Veterans Preference, We're Hiring, Emergency Preparedness Day. Online: social and event graphics.",
  },
  {
    id: 'program',
    title: 'Program Branding',
    description:
      "Materials led by a program logo — here, Medi-Cal. The program logo leads, DHCS stays in a secondary position, and layouts lean on the program's two brand colors plus black for body text.",
    image: {
      src: `${IMAGES}/program-branding@2x.webp`,
      alt: 'Medi-Cal program materials: brochures, flyers, posters, social tiles and responsive web pages',
      width: 440,
      height: 252,
    },
    caption: 'Brochure',
    captionText:
      "Hearing Aid Coverage for Children. Flyer: managed-care complaints, Children's Presumptive Eligibility, services for pregnant people, CAPP Project ECHO. Poster: free temporary Medi-Cal for children and teens. Digital: social tiles (multilingual) and responsive web.",
  },
];

// Numbered 1 to 13 in the flyer image; the legend's counter badges must keep the same order.
const flyerSpecs = [
  { name: 'Eyebrow', spec: 'DHCS Yellow, 12 pt' },
  { name: 'Headline', spec: 'Public Sans Bold, White, 18–20 pt' },
  { name: 'Sub Copy', spec: 'Public Sans Bold, White, 12 pt' },
  { name: 'Program Logo', spec: 'Appears in the top-right corner.' },
  {
    name: 'Header Bar',
    spec: 'DHCS Blue. Height can vary with the length of the headline and sub copy.',
  },
  { name: 'Body Copy', spec: 'Public Sans Regular, Black, 12 pt' },
  {
    name: 'Bullet',
    spec: 'Double arrow (chevron »), DHCS Orange. Public Sans Regular, Black, 12 pt',
  },
  { name: 'Sub Head', spec: 'Public Sans Bold, DHCS Blue or DHCS Teal, 13–18 pt' },
  { name: 'Icon', spec: 'Supporting line icon, DHCS Blue.' },
  { name: 'Call Out', spec: 'Public Sans Bold, DHCS Teal or DHCS Blue, 14–16 pt' },
  {
    name: 'Call Out Bar',
    spec: 'DHCS Yellow. Height can vary with the length of the copy. Used with the Star element.',
  },
  { name: 'Call Out Header', spec: 'Public Sans Bold, Black, 13–16 pt' },
  { name: 'Call Out Sub Copy', spec: 'Public Sans, Black, 12–14 pt' },
];

// The frame links the card to a one-time Microsoft sign-in URL; its redirect target, the DHCS
// SharePoint, is the stable address.
const referenceCards = [
  {
    title: 'DHCS Visual Identity Resources Page',
    subtitle: '[cadhcs.sharepoint.com]',
    linkUrl: REFERENCE_LINKS.dhcsVisualIdentityResources,
  },
];

const BrandInUseStructure = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="content">
      <ContentHeaderStyled
        title="Brand in Use"
        breadcrumbs={[
          { label: 'Branding Styleguide', url: '/branding-styleguide' },
          { label: 'Brand in Use' },
        ]}
      />
      <div className="page-container">
        <div className="page-content">
          <div className="page-content-with-navigation-wrapper">
            <div className="page-content-with-navigation">
              <div className="page-content-item">
                <h2 className="h1" id="overview">
                  Overview
                </h2>
                <h3 className="h3" id="definition">
                  Definition
                </h3>
                <p className="mb-40">
                  How the DHCS identity comes together in real materials — across both department
                  branding (the DHCS logo and voice) and program branding (program logos such as
                  Medi-Cal). These examples show the system applied to the formats teams produce
                  most: flyers, signage, brochures, posters, and digital. Use them as a reference
                  for layout, color balance, and logo placement.
                </p>
              </div>
              <h2 className="divider h1" id="examples">
                Examples
              </h2>
              {examples.map((example) => (
                <section key={example.id}>
                  <div className="page-content-item">
                    <h3 className="example-title" id={example.id}>
                      {example.title}
                    </h3>
                    <p className="mb-40">{example.description}</p>
                  </div>
                  <figure className="example">
                    <div className="example-frame">
                      <img
                        src={example.image.src}
                        alt={example.image.alt}
                        width={example.image.width}
                        height={example.image.height}
                      />
                    </div>
                    <figcaption>
                      <strong>{example.caption}</strong>
                      <p>{example.captionText}</p>
                    </figcaption>
                  </figure>
                </section>
              ))}
              <div className="page-content-item">
                <h3 className="example-title" id="flyer-anatomy">
                  Flyer Anatomy
                </h3>
                <p className="mb-40">
                  The anatomy of a program flyer and the type specs for each element. Numbers on the
                  sample map to the specifications on the right.
                </p>
              </div>
              <div className="anatomy">
                <img
                  src={`${IMAGES}/flyer-anatomy@2x.webp`}
                  alt="Specimen program flyer with thirteen numbered callouts: eyebrow, headline, sub copy, program logo, header bar, body copy, bullets, sub heads, icon, call out, call out bar, call out header and call out sub copy"
                  width={481}
                  height={649}
                />
                <ol className="spec-legend">
                  {flyerSpecs.map((item) => (
                    <li key={item.name}>
                      <div>
                        <strong>{item.name}</strong>
                        <span>{item.spec}</span>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
              <h2 className="h1 divider" id="references">
                References
              </h2>
              <ExternalLinkGroupStyled cards={referenceCards} />
            </div>
            <div>
              <div className="page-navigation">
                <h2>Contents</h2>
                <Scrollspy
                  ids={[
                    'overview',
                    'definition',
                    'examples',
                    'department',
                    'program',
                    'flyer-anatomy',
                    'references',
                  ]}
                  offset={0}
                  activeItemClassName="active"
                  itemContainerClassName="multilevel-link-list"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <PaginationStyled
        previousLink="/branding-styleguide/interactions-and-animations"
        previous="Interactions and Animations"
        nextLink="/general-components"
        next="General Components"
      />
    </div>
  </div>
);

const BrandInUse = styled(BrandInUseStructure)`
  ${commonPageContentStyles}
  ${navigationStyles([2, 4, 5, 6])}

    overflow: unset;
  height: 100%;

  > .content {
    width: 100%;
    padding: 0;
  }

  /* The Updated frame sets the example titles in the H1 style, with the 16px title-to-text gap of a sub-section. */
  .example-title {
    ${textStyle('h1')}
    color: ${({ theme }) => theme.neutralText};
    margin: 0 0 1rem;
  }

  .example-frame {
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    height: 18.75rem;
    padding: 1.5rem 0;
    background: ${({ theme }) => theme.neutralBackgroundMedium};
    border: 1px solid ${({ theme }) => theme.neutralBorder};

    img {
      height: 15.75rem;
      width: auto;
      max-width: 100%;
      object-fit: contain;
    }
  }

  .example figcaption {
    display: grid;
    gap: 0.5rem;
    max-width: 43.75rem;
    letter-spacing: 0;
    color: ${({ theme }) => theme.neutralTextWeak};

    strong {
      ${textStyle('h4Sb')}
    }

    p {
      ${textStyle('paragraphMedium')}
      margin: 0;
    }
  }

  .anatomy {
    display: flex;
    align-items: flex-start;
    gap: 2.5rem;
    margin-bottom: 2.5rem;

    > img,
    > .spec-legend {
      flex: 1 1 0;
      min-width: 0;
    }

    > img {
      height: auto;
    }
  }

  /* The legend's 13px/1.35 text carries no Text Style in the Updated frame, so its raw values are kept (register: Brand in Use legend text). */
  .page-content .spec-legend {
    display: grid;
    gap: 0.875rem;
    margin: 0;
    padding: 0;
    list-style: none;
    counter-reset: spec;

    li {
      display: flex;
      gap: 0.75rem;
      margin: 0;
      counter-increment: spec;

      &::before {
        content: counter(spec);
        flex: none;
        display: grid;
        place-items: center;
        width: 1.375rem;
        height: 1.375rem;
        border-radius: 50%;
        font-size: 0.75rem;
        line-height: 1rem;
        background: ${({ theme }) => theme.badgeFill};
        color: var(--color-brand-white);
      }
    }

    strong {
      display: block;
      margin-bottom: 0.125rem;
      font-size: 0.875rem;
      font-weight: 600;
      line-height: 1.35;
      color: ${({ theme }) => theme.neutralText};
    }

    span {
      display: block;
      margin: 0;
      font-size: 0.8125rem;
      line-height: 1.35;
      color: ${({ theme }) => theme.neutralTextWeak};
    }
  }
`;

export default BrandInUse;
